import { useCallback } from 'react';
import Notification from '@lingoda/ui/icons/Notification';
import { actionClick } from '@lingoda/analytics';
import { useDispatch, useSelector } from '@lingoda/hooks';
import { Badge, Tab, colorsPalette, useUiEvent } from '@lingoda/ui';
import {
    notificationsShowTypeSelector,
    pendingNotificationsSelector,
    toggleAllNotificationsContainer,
} from '@lingoda/notifications';
import { styled } from '@lingoda/ui/theme';
import { useStudentNotificationsQuery } from '@lingoda/graphql';
import type { MouseEventHandler } from 'react';

const NotificationTab = styled(Tab)(({ theme }) => ({
    // overwrite default MUI Tab styles to prevent the icon from being distorted
    maxHeight: theme.spacing(5),
    maxWidth: theme.spacing(5),
    minWidth: theme.spacing(5),
    minHeight: theme.spacing(5),
    overflow: 'visible',
    border: `1px solid ${colorsPalette.blackSolid[20]}`,
    borderRadius: '100%',
    transition: 'background 250ms',

    // target only devices that support hover to prevent sticky hover state on touch devices
    '@media(hover: hover)': {
        '&:hover': {
            backgroundColor: colorsPalette.blackAlpha[4],
        },
    },

    '&:active': {
        backgroundColor: colorsPalette.primaryAlpha[8],
    },
    '&:not(:active):focus': {
        borderColor: colorsPalette.primaryAlpha[40],
        boxShadow: '0px 1px 4px 0px rgba(5, 31, 78, 0.16)',
    },
}));

export const Notifications = () => {
    const addEvent = useUiEvent();
    const dispatch = useDispatch();
    const { data } = useStudentNotificationsQuery();
    const studentNotificationsLength = data?.studentNotification.length ?? 0;
    const notifications = useSelector(pendingNotificationsSelector);
    const notificationsLength = notifications.length + studentNotificationsLength;

    const handleOnClick = useCallback<MouseEventHandler<HTMLDivElement>>(
        (e) => {
            dispatch(toggleAllNotificationsContainer());
            void addEvent(actionClick('Notifications'));
            // force blur after mouse release to prevent leftover border
            e.currentTarget.blur();
        },
        [dispatch, addEvent],
    );
    const notificationsShown = useSelector(notificationsShowTypeSelector);

    if (!notificationsLength) {
        return null;
    }

    return (
        <NotificationTab
            disableRipple
            onClick={handleOnClick}
            aria-label="Notifications tab"
            sx={
                notificationsShown === 'None'
                    ? undefined
                    : {
                          backgroundColor: colorsPalette.primaryAlpha[8],
                      }
            }
            icon={
                <BadgeStyled
                    anchorOrigin={{
                        horizontal: 'right',
                        vertical: 'bottom',
                    }}
                    color="error"
                    overlap="rectangular"
                    badgeContent={notificationsLength}
                    data-cy="Notifications badge"
                >
                    <NotificationStyled />
                </BadgeStyled>
            }
        />
    );
};

const BadgeStyled = styled(Badge)(({ theme }) => ({
    paddingLeft: theme.spacing(1),

    '& .BaseBadge-badge': {
        fontSize: '0.75rem',
        fontWeight: 600,
    },
}));

const NotificationStyled = styled(Notification)({
    marginRight: 8,
    color: colorsPalette.blackSolid[100],
});
